/**
 * What the reconciliation loop wrote — or, in a preview, would write.
 *
 * The report reads top-down the way a reviewer needs it: the verdict line
 * (preview or applied, how many deals, how many writes, how many fields wait
 * on a person), the apply action while it is still a preview, then the per-step
 * totals, then each deal with its writes and the decisions it still owes.
 *
 * Nothing here is an accept-all. The loop applied only what the engine alone
 * ever decided; every row under "needs a person" is a value it refused to
 * touch, and the record it belongs to opens in the Data Intelligence Review where
 * that decision is made field by field.
 */
import { AlertTriangle, CheckCircle2, ChevronRight, Loader2, UserCheck } from 'lucide-react'

import type { ReconcileDeal, ReconcileResponse } from '@/services/ingestion'

/** The step totals worth a line, in the order the loop runs them. */
const STEP_TOTALS: { key: string; label: string }[] = [
  { key: 'reparseAccepted', label: 'Re-parse readings accepted' },
  { key: 'backfilled', label: 'Record values carried onto deals' },
  { key: 'decisionsCarried', label: 'Reviewer decisions carried' },
  { key: 'workbooksRegistered', label: 'Sponsor models registered' },
  { key: 'workbooksMapped', label: 'Sponsor models mapped' },
  { key: 'sponsorFieldsFilled', label: 'Fields filled from the sponsor model' },
  { key: 'schedulesApplied', label: 'Cash-flow schedules applied' },
  { key: 'blobFiguresRecovered', label: 'Figures recovered from blobs' },
  { key: 'counterpartyFilled', label: 'Counterparties filled' },
  { key: 'derived', label: 'Values derived' },
  { key: 'datapointsDiscovered', label: 'Datapoints proposed for the catalog' },
]

const PERSON_KINDS: Record<string, string> = {
  human_decided: 'a person decided this on the record',
  deal_value_set_by_person: 'a person set this on the deal',
  engine_no_longer_supports_value: 'the engine can no longer support this party',
  rival_counterparties: 'the package names rival counterparties',
  no_counterparty_stated: 'no counterparty is stated anywhere',
  stated_disagrees_with_arithmetic: 'a stated figure disagrees with the arithmetic',
  workbook_role_unassigned: 'whose model this is must be assigned',
  workbook_unmapped: 'the model could not be anchored automatically',
  workbook_needs_manual_map: 'the model needs a hand-built cell map',
  source_conflict: 'the package states this more than one way — a person decides',
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`
}

type Props = {
  report: ReconcileResponse
  applying: boolean
  onApply: () => void
  onOpenRecord: (ingestionId: string) => void
  /** Records that currently carry proposals — the ones the panel can open. */
  pendingIds: Set<string>
  /** False for an account that may read the report but not change a deal. */
  canApply?: boolean
}

export default function ReconcileReport({
  report, applying, onApply, onOpenRecord, pendingIds, canApply = true,
}: Props) {
  const writes = report.totals.writes ?? 0
  const needsPerson = report.totals.needsPerson ?? 0
  const reupload = report.needsReupload ?? []
  const steps = STEP_TOTALS.filter((s) => (report.totals[s.key] ?? 0) > 0)

  return (
    <div className="space-y-3" data-testid="reconcile-report">
      <div className="border-b border-amber-400/10 pb-2">
        <h3 className="flex items-center gap-1.5 text-[12px] font-medium text-stone-200">
          {report.applied
            ? <CheckCircle2 size={13} className="text-emerald-300/90" />
            : <AlertTriangle size={13} className="text-amber-300" />}
          {report.applied ? 'Reconciliation applied' : 'Reconciliation preview'}
        </h3>
        <p className="mt-0.5 text-[10px] text-stone-500">
          {plural(report.deals.length, 'deal', 'deals')} of {report.examined} examined ·{' '}
          {plural(writes, 'write', 'writes')} · {needsPerson} need a person ·{' '}
          {report.elapsedSeconds}s · <span className="font-mono">{report.engine}</span>
        </p>
        {!report.complete && report.remaining?.length > 0 && (
          <p className="mt-1 text-[10px] text-amber-300/90">
            {plural(report.remaining.length, 'record was', 'records were')} not reached in this
            run. Run it again to continue — finished deals stay finished.
          </p>
        )}
        <p className="mt-1 text-[10px] leading-relaxed text-stone-400">{report.note}</p>
        {/* nd-001: a deal whose stored package cannot be re-read has no
            re-parse to restore what value integrity takes back. The operator
            re-uploads BEFORE applying; "nothing to apply" is never said over
            packages nobody could read. */}
        {reupload.length > 0 && (
          <div
            data-testid="reconcile-reupload"
            className="mt-2 rounded border border-amber-400/30 bg-amber-400/5 px-2 py-1.5 text-[10px] text-amber-200"
          >
            <p className="font-medium">
              {plural(reupload.length, 'deal', 'deals')} could not be re-read — the stored package is
              unavailable. Re-upload {reupload.length === 1 ? 'it' : 'each'} before applying.
            </p>
            <ul className="mt-1 space-y-0.5 text-amber-100/80">
              {reupload.map((r) => (
                <li key={r.loanId}>
                  <span className="font-mono">{r.loanId}</span>
                  {r.property ? ` ${r.property}` : ''}
                  {r.held ? ' — held: not reconciled against its package by the automated sweep' : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
        {!report.applied && writes > 0 && !canApply && (
          <p className="mt-2 text-[10px] text-stone-500">
            Applying these writes needs Intake edit access, which this account does not hold.
          </p>
        )}
        {!report.applied && writes > 0 && canApply && (
          <button
            type="button"
            onClick={onApply}
            disabled={applying}
            className="mt-2 flex items-center gap-1.5 rounded border border-amber-400/40 bg-amber-400/10 px-2.5 py-1 text-[10px] uppercase tracking-wide text-amber-200 transition-colors hover:bg-amber-400/20 disabled:opacity-50"
          >
            {applying ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
            Apply {plural(writes, 'write', 'writes')} to {plural(report.deals.length, 'deal', 'deals')}
          </button>
        )}
        {!report.applied && writes === 0 && reupload.length === 0 && (
          <p className="mt-2 text-[10px] text-stone-500">
            Every deal already holds what its package states. Nothing to apply.
          </p>
        )}
      </div>

      {steps.length > 0 && (
        <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-0.5 text-[10px]">
          {steps.map((s) => (
            <div key={s.key} className="contents">
              <dt className="text-stone-400">{s.label}</dt>
              <dd className="text-right font-mono text-stone-200">{report.totals[s.key]}</dd>
            </div>
          ))}
        </dl>
      )}

      {report.failed.length > 0 && (
        <div className="rounded border border-red-400/20 bg-red-400/5 px-2 py-1.5 text-[10px] text-red-200">
          {plural(report.failed.length, 'deal failed', 'deals failed')}:{' '}
          {report.failed.map((f) => `${f.loanId ?? f.ingestionId} — ${f.detail}`).join('; ')}
        </div>
      )}

      <div className="space-y-1.5">
        {report.deals.map((deal: ReconcileDeal) => (
          <div key={deal.loanId} className="rounded border border-amber-400/10 bg-black/20 px-2.5 py-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate text-[11px] text-stone-200">
                  {deal.property || deal.subject || deal.loanId}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[10px] text-stone-500">
                  <span className="font-mono">{deal.loanId}</span>
                  <span>·</span>
                  <span>{plural(deal.writeCount, 'write', 'writes')}</span>
                  <span>·</span>
                  <span>{deal.coverage.populated}/{deal.coverage.total} fields</span>
                  <span>·</span>
                  <span>{deal.converged ? `converged in ${plural(deal.passes, 'pass', 'passes')}` : `${deal.passes} passes, not converged`}</span>
                  {typeof deal.seconds === 'number' && (
                    <>
                      <span>·</span>
                      <span>{deal.seconds}s</span>
                    </>
                  )}
                  {deal.coverage.onRecordNotOnDeal.length > 0 && (
                    <span className="text-amber-300/80">
                      · {deal.coverage.onRecordNotOnDeal.length} on the record, not on the deal
                    </span>
                  )}
                </div>
              </div>
              {pendingIds.has(deal.ingestionId) && (
                <button
                  type="button"
                  onClick={() => onOpenRecord(deal.ingestionId)}
                  className="flex flex-shrink-0 items-center gap-0.5 text-[10px] text-amber-300 hover:underline"
                >
                  Decide <ChevronRight size={10} />
                </button>
              )}
            </div>
            {deal.writes.length > 0 && (
              <ul className="mt-1.5 space-y-0.5">
                {deal.writes.slice(0, 12).map((w, i) => (
                  <li key={`${w.step}-${w.field}-${i}`} className="flex items-baseline gap-2 text-[10px]">
                    <span className="w-24 flex-shrink-0 truncate font-mono text-stone-500">{w.step}</span>
                    <span className="text-stone-300">{w.field}</span>
                    <span className="truncate text-stone-500">
                      {w.from === null || w.from === undefined || w.from === '' ? '—' : String(w.from)} → {String(w.to ?? '—')}
                    </span>
                  </li>
                ))}
                {deal.writes.length > 12 && (
                  <li className="text-[10px] text-stone-500">…and {deal.writes.length - 12} more</li>
                )}
              </ul>
            )}
            {deal.needsPerson.length > 0 && (
              <ul className="mt-1.5 space-y-0.5">
                {deal.needsPerson.map((n, i) => (
                  <li key={`${n.kind}-${n.field ?? n.workbook ?? i}`} className="flex items-start gap-1.5 text-[10px] text-amber-300/90">
                    <UserCheck size={10} className="mt-px flex-shrink-0" />
                    <span>
                      <span className="text-stone-300">{n.field ?? n.workbook ?? 'deal'}</span>
                      {' — '}
                      {PERSON_KINDS[n.kind] ?? n.kind}
                      {n.decidedBy ? ` (${n.decidedBy})` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {deal.failed.length > 0 && (
              <p className="mt-1 text-[10px] text-stone-500">{deal.failed.join('; ')}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
